import { PageRequest } from '@/core/request/PageRequest';
import { Moment } from 'moment';
import { PurchaseItemType } from '../Purchase.types';
import { TaxRegimeEnum } from '../../invoice/invoiceNoteItems/invoiceNoteItemsParts/InvoiceNoteItems.types';
import { RegistrationsDefaultCode } from '@/types/Register';
import {
  InvoiceItemTaxIbsCbs,
  InvoiceItemTaxIs,
} from '@/components/Fiscal/Invoice/InvoiceItemModal/PartFormsModal/InvoicePartForm.types';

export enum TaxRegime {
  normalRegime = 'NORMAL_REGIME',
  simpleNational = 'SIMPLE_NATIONAL',
  simpleNationalExceessSublimitGrossRevenue = 'SIMPLE_NATIONAL_EXCESS_SUBLIMIT_GROSS_REVENUE',
}

export interface PurchaseRemoveRequest {
  id: number;
  purchaseId: number;
}

export const purchaseItemTypeText = {
  [PurchaseItemType.part]: 'Peça',
  [PurchaseItemType.consumable]: 'Insumo',
  [PurchaseItemType.task]: 'Serviço Terceirizado',
};

export const purchaseItemTypeColor = {
  [PurchaseItemType.part]: 'blue',
  [PurchaseItemType.consumable]: 'blue',
  [PurchaseItemType.task]: 'yellow',
};

export const PurchaseItemCodeFormName = {
  [RegistrationsDefaultCode.manufacturerCode]: 'manufacturingCode',
  [RegistrationsDefaultCode.internalCode]: 'sku',
  [RegistrationsDefaultCode.barCode]: 'barCode',
  [RegistrationsDefaultCode.companyCode]: 'companyCode',
  [RegistrationsDefaultCode.sku]: 'sku',
};

export interface PurchaseItem {
  amountReturned: number;
  hasKey: boolean;
  cfop: {
    id: number;
  };
  ncm: {
    id: number;
  };
  cfopId: number;
  complementId: number;
  consumableId: number;
  discount: number;
  id: number;
  manufacturerId: number;
  name: string;
  ncmId: number;
  partId: number;
  barCode?: string;
  sku?: string;
  manufacturingCode?: string;
  companyCode?: string;
  manufacturingName: string;
  productCode: string;
  purchaseId: number;
  purchase: {
    active: boolean;
    code: number;
    companyId: number;
    id: number;
    releaseDate: string;
    keyNfe?: string;
  };
  purchaseItemTaxes?: PurchaseItemTaxes;
  quantity: number;
  subGroupId: number;
  totalAmount: number;
  type: PurchaseItemType;
  unitMeasurement: string;
  unitaryValue: number;
  workOrderItemId: number;
  quantityToReturn: number;
  workOrderPurchaseQuotationsId: number;
  workItemId: number;
  workOrderNumber?: string;
  sellPriceCurrent: number;
  sellPriceInformed?: number;
  buyPriceCurrent?: number | null;
}

export interface PurchaseItemTaxes
  extends InvoiceItemTaxIs,
    InvoiceItemTaxIbsCbs {
  additionalValueMarginSt?: number;
  baseCofins?: number;
  baseCofinsSt?: number;
  baseCsll?: number;
  baseFcp?: number;
  baseFcpSt?: number;
  baseIcms?: number;
  baseIcmsSt?: number;
  baseIcmsStWithheld?: number;
  baseInterstateIcms?: number;
  baseIpi?: number;
  baseIssqn?: number;
  basePis?: number;
  basePisSt?: number;
  calcPercentCofins?: boolean | number;
  calcPercentCofinsSt?: boolean | number;
  calcPercentPis?: boolean | number;
  calcPercentPisSt?: boolean | number;
  csosnIcms?: string;
  cstCofins?: string;
  cstIcms?: string;
  cstIpi?: string;
  cstPis?: string;
  deliveryValue?: number;
  expenseValue?: number;
  id?: number;
  insuranceValue?: number;
  modBcIcms?: number;
  modBcIcmsSt?: number;
  motDesIcms?: number;
  originIcms?: number;
  percentFcp?: number;
  percentProvisionalParticipation?: number;
  quotaBcOp?: number;
  quotaCofins?: number;
  quotaCofinsSt?: number;
  quotaCsll?: number;
  quotaDif?: number;
  quotaFcp?: number;
  quotaFcpSt?: number;
  quotaIcms?: number;
  quotaIcmsSt?: number;
  quotaIcmsUfDestination?: number;
  quotaInterstateIcms?: number;
  quotaIpi?: number;
  quotaIrpj?: number;
  quotaIssqn?: number;
  quotaMvaSt?: number;
  quotaPis?: number;
  quotaPisSt?: number;
  reductionIcms?: number;
  reductionIcmsSt?: number;
  taxRegime: TaxRegimeEnum;
  ufSt?: string;
  valueCofins?: number;
  valueCofinsSt?: number;
  valueCsll?: number;
  valueFcp?: number;
  valueFcpSt?: number;
  valueIcms?: number;
  valueIcmsDif?: number;
  valueIcmsFcp?: number;
  valueIcmsOp?: number;
  valueIcmsSt?: number;
  valueIcmsStWithheld?: number;
  valueIcmsUnencumbered?: number;
  valueInterstateDestinationIcms?: number;
  valueInterstateOriginIcms?: number;
  valueIpi?: number;
  valueIrpj?: number;
  valueIssqn?: number;
  valuePis?: number;
  valuePisSt?: number;
  valueTotal?: number;
  valueUnityCofins?: number;
  valueUnityCofinsSt?: number;
  valueUnityPis?: number;
  valueUnityPisSt?: number;
  valueBaseOfImportTax?: number;
  valueOfCustomsExpenses?: number;
  valueImportTax?: number;
  amountOfTaxOnFinancialTransactions?: number;
}

export interface PurchaseItemsFormValues {
  amountReturned: number;
  cfopId: number;
  complementId: number;
  consumableId: number;
  discount: number;
  id: number;
  manufacturerId: number;
  name: string;
  ncmId: number;
  partId: number;
  productCode: string;
  purchaseId: number;
  purchaseItemTaxes: PurchaseItemTaxes;
  quantity: number;
  subGroupId: number;
  totalAmount: number;
  type: PurchaseItemType;
  unitMeasurement: string;
  unitaryValue: number;
  workOrderItemId: number;
  workOrderNumber: string;
}

export interface CreatePurchaseItemRequest {
  amountReturned?: number;
  cfopId?: number;
  complementId: number;
  consumableId?: number;
  discount?: number;
  manufacturerId: number;
  name: string;
  ncmId?: number;
  partId?: number;
  productCode?: string;
  purchase: number;
  purchaseItemTaxes?: PurchaseItemTaxes;
  quantity: number;
  subGroupId?: number;
  totalAmount?: number | null;
  type: PurchaseItemType;
  unitMeasurement: string;
  unitaryValue: number;
  workOrderItemId: number;
  workOrderNumber: string;
  workOrderPurchaseQuotations?: {
    id: number;
    quantitySentPurchase: number;
    quantitypurchased: number;
    requestedQuantity: number;
  };
}

export interface UpdatePurchaseItemRequest {
  amountReturned?: number;
  cfopId: number;
  complementId: number;
  consumableId?: number;
  discount: number;
  id: number;
  manufacturerId: number;
  name: string;
  ncmId: number;
  partId?: number;
  productCode: string;
  purchase: number;
  purchaseItemTaxes?: Omit<
    PurchaseItemTaxes,
    'competenceApuration' | 'ibsZfmAssessmentPeriod'
  > & {
    competenceApuration?: string;
    ibsZfmAssessmentPeriod?: string;
  };
  quantity: number;
  subGroupId: number;
  totalAmount: number;
  type: PurchaseItemType;
  unitMeasurement: string;
  unitaryValue: number;
  workOrderItemId: number;
  barCode?: string;
  cfop?: {
    billing?: boolean;
    code?: string;
    description?: string;
    id?: number;
    inState?: boolean;
    output?: boolean;
    outsideCountry?: boolean;
  };
  consumableDTO?: {
    active?: boolean;
    barCode?: string;
    cfopId?: number;
    comment?: string;
    companyCode?: string;
    id?: number;
    imageUrl?: string;
    lifespan?: number;
    manufacturerId?: number;
    manufacturingCode?: string;
    name?: string;
    ncmId?: number;
    sku?: string;
    subGroupId?: number;
    unit?: string;
    unitAlternative?: string;
    unitConversionFactor?: number;
  };
  duplicateConsumableBarcode?: boolean;
  duplicatePartBarcode?: boolean;
  itemNumber?: number;
  keyNfe?: string;
  linked?: boolean;
  ncm?: {
    active?: boolean;
    aliquotCofins?: number;
    aliquotIcms?: number;
    aliquotIpi?: number;
    aliquotPis?: number;
    calcPercentCofins?: boolean;
    cest?: string;
    code?: number;
    csosnIcms?: string;
    cstCofins?: string;
    cstCofinsEntry?: string;
    cstIcms?: string;
    cstIcmsEntry?: string;
    cstIpi?: string;
    cstIpiEntry?: string;
    cstPis?: string;
    cstPisEntry?: string;
    description?: string;
    id?: number;
    modalityDeterminationBcIcms?: number;
    mva?: number;
    reductionIcms?: number;
    taxesPercentage?: number;
  };
  partDTO?: {
    active?: boolean;
    barCode?: string;
    cfopId?: number;
    comment?: string;
    companyCode?: string;
    flag?: string;
    id?: number;
    imageUrl?: string;
    includeInDiagnosis?: boolean;
    kmToMaintenance?: number;
    lifespan?: number;
    localizaCode?: string;
    manufacturerId?: number;
    manufacturingCode?: string;
    name?: string;
    ncmId?: number;
    sku?: string;
    subGroupId?: number;
    unit?: string;
    unitAlternative?: string;
    unitConversionFactor?: number;
  };
  quantityToReturn?: number;
  reservationId?: string;
  workOrderNumber?: string;
  workOrderPurchaseQuotations?: {
    id?: number;
    quantitySentPurchase?: number;
    quantitypurchased?: number;
    requestedQuantity?: number;
  };
}

export interface ItemsPurchaseRequest extends PageRequest {
  purchaseId: number;
  enabled?: boolean;
}

export interface ReturnItemFormValues {
  companyId: number;
  supplier: {
    id: number;
    name: string;
  };
  releaseDateStart: Moment;
  releaseDateEnd: Moment;
  items: PurchaseItem[];
}

export interface ReturnItemQuery extends PageRequest {
  companyId?: number;
  supplierId?: number;
  releaseDateStart?: string;
  releaseDateEnd?: string;
  supplierName?: string;
  purchaseId?: number;
}

export enum UpdateIpiValueInInvoiceItemEnum {
  IPI_RETURNED = 'IPI_RETURNED',
  IPI_NORMAL = 'IPI_NORMAL',
  NONE_OPTIONS = 'NONE_OPTIONS',
}

export interface ReturnItemGenerateInvoice {
  amountReturned: number;
  cfop?: {
    billing?: boolean;
    code?: string;
    description?: string;
    id?: number;
    inState?: boolean;
    output?: boolean;
    outsideCountry?: boolean;
  };
  complementId: number;
  consumableId: number;
  discount: number;
  id: number;
  manufacturerId: number;
  name: string;
  ncm?: {
    active?: boolean;
    aliquotCofins?: number;
    aliquotIcms?: number;
    aliquotIpi?: number;
    aliquotPis?: number;
    calcPercentCofins?: boolean;
    cest?: string;
    code?: number;
    csosnIcms?: string;
    cstCofins?: string;
    cstCofinsEntry?: string;
    cstIcms?: string;
    cstIcmsEntry?: string;
    cstIpi?: string;
    cstIpiEntry?: string;
    cstPis?: string;
    cstPisEntry?: string;
    description?: string;
    id?: number;
    modalityDeterminationBcIcms?: number;
    mva?: number;
    reductionIcms?: number;
    taxesPercentage?: number;
  };
  partId: number;
  productCode: string;
  purchase: number;
  purchaseItemTaxes?: PurchaseItemTaxes;
  quantity: number;
  subGroupId: number;
  totalAmount: number;
  type: PurchaseItemType;
  unitMeasurement: string;
  unitaryValue: number;
  workOrderItemId: number;
  workOrderPurchaseQuotations?: {
    id?: number;
    itemId?: number;
    quantitySentPurchase?: number;
    quantitypurchased?: number;
    requestedQuantity?: number;
    workOrderNumber?: string;
  };
  keyNfe?: string;
}

export interface ReturnItemGenerateInvoiceRequest {
  generateReferencedDocument: boolean;
  updateIpiValueInInvoiceItem: UpdateIpiValueInInvoiceItemEnum;
  purchaseItemsRequestDTO: ReturnItemGenerateInvoice[];
}

export interface ItemsPurchaseReturnAllRequest extends PageRequest {
  supplierId: number;
}

export interface ItemsPurchaseReturnAll {
  companyName: string;
  id: number;
  invoice: number;
  invoiceKey: string;
  purchaseCode: number;
  purchaseItemId: number;
  purchaseItemName: string;
  quantity: number;
  value: number;
}

export interface GenerateInvoiceReturnResponse {
  invoiceId: number;
}

export interface PurchaseItemsTags {
  amountReturned: number;
  barCode: string;
  buyPrice: number;
  companyCode: string;
  consumableId: number;
  discount: number;
  id: number;
  issueDate: string;
  location: string;
  manufacturingCode: string;
  manufacturingName: string;
  name: string;
  partBarCode: string;
  partId: number;
  partName: string;
  quantity: number;
  sku: string;
  totalAmount: number;
  type: string;
  unitaryValue: number;
  workOrderNumber: string;
}

export interface PurchaseItemTagsData {
  data: PurchaseItemsTags[];
}

export type PurchaseItemSellPriceUpdateRequest = {
  id: number;
  sellPriceInformed: number;
};

export interface PurchaseItemUpdateByPartId {
  purchaseItemId: number;
  partId: number;
}

export interface PurchaseItemWorkOrderLink {
  purchaseItemId: number;
  workOrderItemId: number;
  quantity: number;
}

export interface PurchaseItemAddWorkOrderItems {
  purchaseItemIds: number[];
}
